// Client-side capture buffer for a curated set of user actions (Record,
// Generate, Sync, section mic, Save, Sign, etc.). Collects events in memory and
// flushes them in batches to the LogUserActivity backend process. Purpose is
// debugging / activity visibility ("did the user actually click X"), not
// analytics or compliance.
//
// Safety: this module must NEVER break a real user action. logActivity and
// flushActivityLog are fully wrapped; a failed flush drops the batch. Only
// whitelisted ctx keys are ever sent (PHI guard) -- see ALLOWED_CTX_KEYS.
//
// Note: logUserActivity is resolved lazily at flush-time (not at module load)
// so that Jest test environments that cannot transform api.js's binary
// transitive dependencies can still exercise this module via __setSendForTests.
// The production code path is identical -- sendFn resolves to logUserActivity
// on first flush (or immediately if __setSendForTests was not called).

const FLUSH_MS = 15000;
const MAX_BUFFER = 50;
// PHI guard: only these structured ctx keys are ever transmitted. Add keys here
// deliberately; anything else a caller passes is dropped by construction.
// `previousAssignee` is a CLINICIAN user id (the encounter's owner before a
// mobile auto-assign transfer), never patient data -- it is the one field that
// makes the encounter.autoAssign event answer "taken from whom", which is the
// whole point of auditing an ownership change on a medical record.
// `reason` (a fixed identity-guard code) and `recordingId` (a random UUID) make
// recording.parked / recording.override answer "why" and "which audio".
const ALLOWED_CTX_KEYS = ["section", "previousAssignee", "reason", "recordingId"];

let buffer = [];
let sendFn = null; // resolved lazily on first flush; overrideable via __setSendForTests
let started = false;
let intervalId = null;
// Named handler refs so __resetForTests can detach them (avoids listener buildup
// across tests on shared jsdom -- TEST-006). No production effect: initActivityLog
// runs once behind the `started` guard and __resetForTests is test-only.
let visibilityHandler = null;
let pagehideHandler = null;

function getDefaultSendFn() {
  // Lazy require avoids pulling heic-to (api.js transitive dep) at module load,
  // which crashes Jest's transform. At runtime the module is fully loaded before
  // any flush fires, so there is no behavioral difference.
  // eslint-disable-next-line global-require
  const mod = require("../services/api");
  const apiObj = mod && mod.default ? mod.default : mod;
  return apiObj.logUserActivity;
}

// The acting user's identity for attribution ("who clicked"). Reuses the same
// "user" login-email key useAuth persists and recordingViewerKey.js reads --
// synchronous and module-accessible, no React hook needed. Captured at log time
// (not flush time) so a logout between click and flush can't mis-attribute.
function safeGetActingUser() {
  try {
    return localStorage.getItem("user") || null;
  } catch (_) {
    return null;
  }
}

// The acting org (tenant) at the moment of the click. Captured at log time and
// re-checked at flush time to enforce tenant isolation: a batch buffered while
// org A was active must never flush into org B's LogUserActivity process (e.g.
// a logout->login into a different org inside the pre-flush window). Mirrors
// safeGetActingUser -- reads the same "organization" key useAuth persists.
function safeGetActingOrg() {
  try {
    return localStorage.getItem("organization") || null;
  } catch (_) {
    return null;
  }
}

export function logActivity(name, ctx = {}) {
  try {
    if (!name) return;
    const c = ctx || {};
    const chatId = c.chatId != null ? String(c.chatId) : null;
    const safeCtx = {};
    for (let i = 0; i < ALLOWED_CTX_KEYS.length; i++) {
      const k = ALLOWED_CTX_KEYS[i];
      if (c[k] != null) safeCtx[k] = c[k];
    }
    buffer.push({ name: String(name), ts: Date.now(), chatId, ctx: safeCtx, user: safeGetActingUser(), org: safeGetActingOrg() });
    if (buffer.length >= MAX_BUFFER) flushActivityLog();
  } catch (_) {
    // never throw from the capture path
  }
}

export function flushActivityLog() {
  try {
    if (buffer.length === 0) return;
    // Drain the buffer up front so it can NEVER grow without bound. If we bail
    // below (no org, or every event filtered out) the batch is dropped, not
    // retained: retaining a no-org batch was unbounded -- the MAX_BUFFER cap keeps
    // re-triggering a flush that returned before clearing -- and pointless, since a
    // null-org event's captured org can never match a real org at send time, so it
    // is undeliverable either way. (Review C1.)
    const batch = buffer;
    buffer = [];
    // Tenant isolation: resolve the org active RIGHT NOW. With no valid org
    // context (e.g. a storage-cleared / logged-out window) we must not emit a
    // "null:LogUserActivity" call -- drop the batch and return. (VER-001/SEC-003.)
    const currentOrg = safeGetActingOrg();
    if (!currentOrg) return;
    // Only send events captured while the CURRENT org was active. Events from any
    // other tenant are dropped -- they can never be delivered to the current org's
    // process without leaking one org's activity into another's vertex. Fixed
    // identity fields (e/ts/u) are written AFTER the ctx spread so an allowlisted
    // ctx key can never shadow the event name, timestamp, or acting user (review
    // C6). Each event's JSON.stringify is isolated so one un-stringifiable event
    // can never drop the whole batch (SF-1 / TEST-005).
    const events = [];
    for (let i = 0; i < batch.length; i++) {
      const e = batch[i];
      if (e.org !== currentOrg) continue;
      try {
        events.push({
          chatId: e.chatId,
          message: JSON.stringify({ ...e.ctx, e: e.name, ts: e.ts, u: e.user }),
        });
      } catch (_) {
        // skip just this event; never drop the rest of the batch
      }
    }
    if (events.length === 0) return;
    const fn = sendFn !== null ? sendFn : getDefaultSendFn();
    Promise.resolve(fn(events)).catch(() => {});
  } catch (_) {
    // never throw from the flush path
  }
}

export function initActivityLog() {
  if (started) return;
  started = true;
  try {
    intervalId = setInterval(flushActivityLog, FLUSH_MS);
    visibilityHandler = () => {
      if (document.visibilityState === "hidden") flushActivityLog();
    };
    document.addEventListener("visibilitychange", visibilityHandler);
    // pagehide (not beforeunload): pagehide fires on the bfcache path too and,
    // unlike beforeunload, does not disqualify the page from the back/forward
    // cache. visibilitychange->hidden is the primary flush; pagehide is the
    // last-gasp fallback. See MDN sendBeacon "Avoid unload and beforeunload".
    pagehideHandler = flushActivityLog;
    window.addEventListener("pagehide", pagehideHandler);
  } catch (_) {
    // if listeners can't attach, the timer flush still runs
  }
}

// test-only helpers
export function __setSendForTests(fn) {
  sendFn = fn;
}
export function __getBufferLengthForTests() {
  return buffer.length;
}
export function __resetForTests() {
  buffer = [];
  started = false;
  if (intervalId) clearInterval(intervalId);
  intervalId = null;
  if (visibilityHandler) document.removeEventListener("visibilitychange", visibilityHandler);
  if (pagehideHandler) window.removeEventListener("pagehide", pagehideHandler);
  visibilityHandler = null;
  pagehideHandler = null;
  sendFn = null; // resets to lazy-resolve logUserActivity on next flush
}
